import { useSyncExternalStore } from "react";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/* Per-account thumbs up/down on songs. Dislikes are excluded from dynamic playlists & AI mixes; likes bias them. */
let state = { liked: new Set(), disliked: new Set(), loaded: false };
const subs = new Set();
const emit = () => subs.forEach((fn) => fn());
let inflight = null;

export const loadRatings = () => {
  if (inflight) return inflight;
  inflight = api.get("/ratings").then(({ data }) => {
    state = { liked: new Set(data.liked), disliked: new Set(data.disliked), loaded: true };
    emit();
  }).catch(() => {}).finally(() => { inflight = null; });
  return inflight;
};
export const resetRatings = () => { state = { liked: new Set(), disliked: new Set(), loaded: false }; emit(); };

export const useRatings = () => {
  const s = useSyncExternalStore((fn) => { subs.add(fn); return () => subs.delete(fn); }, () => state);
  if (!s.loaded && !inflight) loadRatings();
  return s;
};
export const ratingOf = (s, id) => (s.liked.has(id) ? 1 : s.disliked.has(id) ? -1 : 0);

export const rateTrack = async (id, rating) => {
  const prev = state;
  const liked = new Set(prev.liked), disliked = new Set(prev.disliked);
  liked.delete(id); disliked.delete(id);
  if (rating === 1) liked.add(id);
  if (rating === -1) disliked.add(id);
  state = { ...prev, liked, disliked };
  emit();
  if (rating === -1) window.dispatchEvent(new CustomEvent("track-disliked", { detail: id }));
  try { await api.put(`/ratings/${id}`, { rating }); }
  catch (e) { state = prev; emit(); toast.error(formatApiError(e)); }
};

/* Thumbs pair. `size` icon px; `tone` light|dark controls idle colours. */
export const Thumbs = ({ trackId, size = 14, tone = "light", className = "", testid = `rate-${trackId}` }) => {
  const s = useRatings();
  const r = ratingOf(s, trackId);
  const idle = tone === "dark" ? "text-zinc-400 hover:text-white" : "text-zinc-300 hover:text-zinc-900";
  const btn = (val, Icon, label, on) => (
    <button type="button" onClick={(e) => { e.stopPropagation(); rateTrack(trackId, r === val ? 0 : val); }} title={label} aria-label={label} aria-pressed={r === val}
      data-testid={`${testid}-${val === 1 ? "up" : "down"}`} data-state={r === val ? "on" : "off"}
      className={`rounded p-1 transition-colors ${r === val ? on : idle}`}>
      <Icon size={size} fill={r === val ? "currentColor" : "none"} />
    </button>
  );
  return (
    <span className={`flex items-center ${className}`} data-testid={testid} data-rating={r}>
      {btn(1, ThumbsUp, "Thumbs up — more like this in dynamic playlists", "text-emerald-600")}
      {btn(-1, ThumbsDown, "Thumbs down — keep out of dynamic playlists", "text-rose-600")}
    </span>
  );
};
